<script setup lang="ts">
import { ref, watch } from 'vue'
import { useMixStore, STATIC_BASE_URL } from '../../stores/mixStore'
import type { MixTrackDto } from '../../dtos'
import { audioEngine } from '../../audio/AudioEngine'

const props = withDefaults(defineProps<{
  track: MixTrackDto
  readonly?: boolean
}>(), {
  readonly: false
})

const mixStore = useMixStore()

const localVolume = ref(props.track.volume * 100)
const localPan = ref(props.track.pan * 100)

watch(() => props.track.volume, (newVal) => localVolume.value = newVal * 100)
watch(() => props.track.pan, (newVal) => localPan.value = newVal * 100)

const snapPan = (v: number | string) => { const n = Number(v); return Math.abs(n) <= 10 ? 0 : n }
const snapVolume = (v: number | string) => { const n = Number(v); return Math.abs(n - 100) <= 10 ? 100 : n }

const handleVolumeInput = () => {
  localVolume.value = snapVolume(localVolume.value)
  const anySolo = mixStore.currentMix?.tracks.some(t => t.isSolo) ?? false
  const silenced = props.track.isMuted || (anySolo && !props.track.isSolo)
  audioEngine.updateTrackVolume(props.track.id, localVolume.value / 100, silenced)
}

const handleVolumeCommit = () => {
  localVolume.value = snapVolume(localVolume.value)
  mixStore.updateTrack(props.track.id, { volume: localVolume.value / 100 })
}

const handlePanInput = () => {
  localPan.value = snapPan(localPan.value)
  audioEngine.updateTrackPan(props.track.id, localPan.value / 100)
}

const handlePanCommit = () => {
  localPan.value = snapPan(localPan.value)
  mixStore.updateTrack(props.track.id, { pan: localPan.value / 100 })
}

const toggleMute = () => {
  mixStore.updateTrack(props.track.id, { isMuted: !props.track.isMuted })
}

const toggleSolo = () => {
  mixStore.updateTrack(props.track.id, { isSolo: !props.track.isSolo })
}

const downloadingTrack = ref(false)

const handleDownloadTrack = async () => {
  if (downloadingTrack.value || !mixStore.currentMix) return
  downloadingTrack.value = true
  try {
    const durationSeconds = mixStore.currentMix.durationSeconds ?? 0
    if (durationSeconds <= 0) return
    const blob = await audioEngine.renderTrackOffline(props.track, durationSeconds, STATIC_BASE_URL)
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${props.track.name.replace(/[^a-zA-Z0-9_-]/g, '_').toLowerCase()}.wav`
    a.click()
    URL.revokeObjectURL(url)
  } catch (error) {
    console.error('Failed to render track:', error)
  } finally {
    downloadingTrack.value = false
  }
}

</script>

<template>
  <div class="flex flex-col w-full bg-white rounded-2xl p-3 border border-slate-300 shadow-sm select-none lg:h-28 space-y-2">
    <!-- Top Row: Track Name and Delete Button -->
    <div class="flex items-center justify-between px-1">
      <p class="text-xs font-medium text-slate-900 uppercase truncate tracking-tight">{{ track.name }}</p>
      <div class="flex items-center gap-0.5">
        <Button
          icon="pi pi-download"
          severity="secondary"
          text
          rounded
          size="small"
          class="opacity-60 hover:opacity-100 h-6 w-6"
          :class="{ 'animate-pulse !opacity-100': downloadingTrack }"
          v-tooltip.top="'Download Track'"
          @click="handleDownloadTrack"
        />
      </div>
    </div>

    <!-- Desktop: Single row controls -->
    <div class="hidden lg:flex items-center gap-4 px-1">
      <!-- Solo / Mute -->
      <div class="flex gap-1 shrink-0">
        <button
          class="text-[10px] font-black w-8 py-1.5 rounded-md transition-all border"
          :class="track.isSolo ? 'bg-teal-500 text-white border-teal-600' : 'bg-slate-50 text-slate-400 border-slate-400 hover:bg-slate-100'"
          @click="toggleSolo"
        >S</button>
        <button
          class="text-[10px] font-black w-8 py-1.5 rounded-md transition-all border"
          :class="track.isMuted ? 'bg-teal-500 text-white border-teal-600' : 'bg-slate-50 text-slate-400 border-slate-400 hover:bg-slate-100'"
          @click="toggleMute"
        >M</button>
      </div>

      <!-- Pan -->
      <div class="flex flex-col items-center w-16 shrink-0">
        <input
          type="range"
          v-model="localPan"
          min="-100"
          max="100"
          class="w-full h-1 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-teal-500 pan-slider"
          @input="handlePanInput"
          @change="handlePanCommit"
        />
        <div class="flex justify-between w-full mt-1 text-[9px] font-mono text-slate-400 font-bold">
          <span>L</span>
          <span>R</span>
        </div>
      </div>

      <!-- Volume Fader Group -->
      <div class="flex-1 flex flex-col items-end gap-1">
        <div class="w-full h-7 bg-slate-100 rounded-lg border border-slate-400 relative overflow-hidden flex items-center px-2">
          <!-- DB Marks -->
          <div class="absolute inset-0 flex justify-between px-4 pointer-events-none opacity-60">
            <div v-for="i in 10" :key="i" class="h-full border-l border-slate-300 vol-mark"></div>
          </div>

          <input
            type="range"
            v-model="localVolume"
            min="0"
            max="150"
            step="1"
            class="flex-1 h-full appearance-none bg-transparent cursor-pointer relative z-10 custom-slider"
            @input="handleVolumeInput"
            @change="handleVolumeCommit"
          />
        </div>
        <p class="text-[11px] font-black text-teal-600 uppercase tracking-tighter">{{ Math.round(localVolume) }}%</p>
      </div>
    </div>

    <!-- Mobile: Two row controls -->
    <div class="lg:hidden space-y-2 px-1">
      <!-- Row 1: S, M + Pan -->
      <div class="flex items-center gap-3">
        <div class="flex gap-1 shrink-0">
          <button
            class="text-[10px] font-black w-8 py-1.5 rounded-md transition-all border"
            :class="track.isSolo ? 'bg-teal-500 text-white border-teal-600' : 'bg-slate-50 text-slate-400 border-slate-400 hover:bg-slate-100'"
            @click="toggleSolo"
          >S</button>
          <button
            class="text-[10px] font-black w-8 py-1.5 rounded-md transition-all border"
            :class="track.isMuted ? 'bg-teal-500 text-white border-teal-600' : 'bg-slate-50 text-slate-400 border-slate-400 hover:bg-slate-100'"
            @click="toggleMute"
          >M</button>
        </div>
        <div class="flex flex-col items-center flex-1">
          <input
            type="range"
            v-model="localPan"
            min="-100"
            max="100"
            class="w-full h-1 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-teal-500 pan-slider"
            @input="handlePanInput"
            @change="handlePanCommit"
          />
          <div class="flex justify-between w-full mt-1 text-[9px] font-mono text-slate-400 font-bold">
            <span>L</span>
            <span>R</span>
          </div>
        </div>
      </div>

      <!-- Row 2: Volume -->
      <div class="flex items-center gap-2">
        <div class="flex-1 h-7 bg-slate-100 rounded-lg border border-slate-400 relative overflow-hidden flex items-center px-2">
          <div class="absolute inset-0 flex justify-between px-4 pointer-events-none opacity-60">
            <div v-for="i in 10" :key="i" class="h-full border-l border-slate-300 vol-mark"></div>
          </div>
          <input
            type="range"
            v-model="localVolume"
            min="0"
            max="150"
            step="1"
            class="flex-1 h-full appearance-none bg-transparent cursor-pointer relative z-10 custom-slider"
            @input="handleVolumeInput"
            @change="handleVolumeCommit"
          />
        </div>
        <p class="text-[11px] font-black text-teal-600 uppercase tracking-tighter shrink-0">{{ Math.round(localVolume) }}%</p>
      </div>
    </div>

  </div>
</template>

<style>
.custom-slider::-webkit-slider-thumb {
  appearance: none;
  width: 12px;
  height: 22px;
  background: #14b8a6;
  border: 1px solid #0d9488;
  border-radius: 4px;
  box-shadow: 0 2px 4px -1px rgb(0 0 0 / 0.15);
  cursor: pointer;
}

.dark-mode .custom-slider::-webkit-slider-thumb {
  background: #0d9488;
  border-color: #0f766e;
  box-shadow: 0 2px 4px -1px rgb(0 0 0 / 0.4);
}

.pan-slider {
  height: 4px;
}

.pan-slider::-webkit-slider-thumb {
  appearance: none;
  width: 12px;
  height: 12px;
  background: #14b8a6;
  border-radius: 50%;
  border: 1px solid #0d9488;
  cursor: pointer;
  margin-top: -4px;
}

.pan-slider::-webkit-slider-runnable-track {
  background: #cbd5e1;
  height: 4px;
  border-radius: 9999px;
}

.dark-mode .pan-slider::-webkit-slider-runnable-track {
  background: #475569;
}

.dark-mode .pan-slider::-webkit-slider-thumb {
  background: #0d9488;
  border-color: #0f766e;
}

.dark-mode .vol-mark {
  border-color: #475569 !important;
}
</style>
